import { useEffect, useState, useMemo, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { customInstance } from "@/lib/axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { financedPrincipalForPayment } from "@/utils/financedPrincipal";
import { tierTermMonthsOrDefault } from "@/utils/prequalTerms";
import { PAYMENT_ESTIMATE_DISCLAIMER } from "@/utils/paymentEstimateDisclaimer";
import {
  CADENCE_LABELS,
  cadenceConfigFromOffer,
  computePaymentScheduleQuote,
  enabledCadencesFromOffer,
  preferredCadenceFromOffer,
  type PaymentCadence,
} from "@/utils/paymentSchedule";
import {
  SeasonalPaymentRequestButton,
  SeasonalPaymentRequestModal,
  type SeasonalPaymentRequestRecord,
} from "@/components/seasonal-payment-request-modal";

function monthlyPayment(principal: number, annualRatePct: number, termMonths: number): number {
  if (principal <= 0 || termMonths <= 0) return 0;
  const r = annualRatePct / 100 / 12;
  if (r === 0) return principal / termMonths;
  return principal * (r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1);
}

/**
 * Simple interest %: customer sees pre-points loan amount (principal).
 * Payment is based on principal * (1 + points). Interest = total paid - principal (pre-points).
 * annual $ interest = (PMT * n - principal) / (n/12), then rate = annual $ / principal.
 */
function simpleInterestPct(
  principalPrePoints: number,
  monthlyPmt: number,
  termMonths: number
): number {
  if (principalPrePoints <= 0 || termMonths <= 0) return 0;
  const n = termMonths;
  const termYears = n / 12;
  const totalPaid = monthlyPmt * n;
  const annualInterestDollars = (totalPaid - principalPrePoints) / termYears;
  return 100 * (annualInterestDollars / principalPrePoints);
}

/**
 * APR: Excel RATE(nper, -pmt, pv, 0, 0) * 12.
 * Solves for monthly r where pv = pmt * ((1 - (1+r)^-n) / r), then APR = r * 12 as %.
 * Step 1: pv = customer loan (pre-points), pmt = payment on principal*(1+points), nper = term months.
 * Step 2: target = pv / pmt (annuity factor we need).
 * Step 3: find r so (1-(1+r)^-n)/r = target; APR = r*12*100.
 */
function finalAprIncludingPoints(
  principalPrePoints: number,
  noteRatePct: number,
  termMonths: number,
  pointsPct: number
): number {
  if (principalPrePoints <= 0 || termMonths <= 0) return noteRatePct;
  const principalWithPoints = principalPrePoints * (1 + pointsPct / 100);
  const payment = monthlyPayment(principalWithPoints, noteRatePct, termMonths);
  if (payment <= 0) return noteRatePct;
  const nper = termMonths;
  const pmt = payment;
  const pv = principalPrePoints;
  const target = pv / pmt; // (1 - (1+r)^-n) / r = target
  let iLow = 1e-6;
  let iHigh = 0.5;
  for (let iter = 0; iter < 120; iter++) {
    const iMid = (iLow + iHigh) / 2;
    const annuity = (1 - Math.pow(1 + iMid, -nper)) / iMid;
    if (annuity > target) iLow = iMid; // need higher r (annuity decreases as r increases)
    else iHigh = iMid;
  }
  const monthlyRate = (iLow + iHigh) / 2;
  return monthlyRate * 12 * 100;
}

interface PrequalResult {
  lender_id: number;
  tier_id: number;
  lender_name: string;
  tier_name: string;
  matched: boolean;
  failure_reason?: string;
  failure_code?: string;
  rate_min: number;
  rate_max: number;
  term_months: number[];
  total_points_used?: number;
  min_down_payment_pct?: number | null;
  min_down_payment_amount?: number | null;
  equipment_requirements?: string | null;
  payment_range?: Record<number, { min_payment: number; max_payment: number }>;
  required_stipulation_templates?: { id: number; name: string }[];
  approval_card_note?: string | null;
  ownership_100_pct_note?: string | null;
  display_bucket?: string | null;
  display_rank_in_bucket?: number | null;
  requires_advance_payment?: boolean;
  enabled_cadences?: string[];
  non_monthly_cadences?: Record<string, { advance_payment?: boolean; defer_first_months?: number }>;
}

interface PrequalLeverAnswers {
  prior_1099_or_sole_prop?: boolean;
  prior_1099_year?: number | string;
  other_company_similar_industry?: boolean;
  other_company_notes?: string;
  updated_years_in_business?: number | string;
  other_company_years_in_business?: number | string;
  new_business_name?: string;
  submitted_at?: string;
}

interface CreditSnippet {
  fico_score?: number | null;
  time_in_bureau?: string;
  revolving_usage_pct?: number | null;
  comparable_installment_debt_24m?: number | null;
  mortgage_balance?: number | null;
  chargeoffs_display?: string;
  collections_display?: string;
  repos_foreclosures_display?: string;
}

interface PrequalContext {
  company_name?: string;
  industry?: string;
  equipment_name?: string;
  time_in_bureau_display?: string;
  credit_snippet?: CreditSnippet;
  credit_snippets?: { display_name: string; credit_snippet: CreditSnippet }[];
}

interface PrequalData {
  stage?: string;
  show_prequals_after_submit?: boolean;
  show_prequal_interest_and_apr?: boolean;
  redirect_after_apply_url?: string | null;
  prequal_results?: { run_at?: string; results?: PrequalResult[]; display_offers?: PrequalResult[]; manual_review_flags?: string[] };
  prequalified_at?: string | null;
  equipment_price?: number | null;
  requested_amount?: number | null;
  prequal_selection?: Record<string, unknown> | null;
  equipment_name?: string;
  company_name?: string;
  years_in_business?: number | null;
  industry?: string;
  prequal_context?: PrequalContext;
  organization_logo_url?: string | null;
  prequal_lever_answers?: PrequalLeverAnswers | null;
  manual_review_flags?: string[];
  credit_pull_status?: string | null;
  credit_pull_error?: string | null;
  upload_link_url?: string | null;
  manual_review_requested?: boolean;
  submission_mode?: string | null;
  seasonal_payment_eligible?: boolean;
  seasonal_payment_requests?: SeasonalPaymentRequestRecord[];
  seasonal_payment_lender_ids?: number[];
  equipment_intake_pending?: boolean;
  intake_phase?: string;
  cadence_picker?: boolean;
  intake_program?: {
    intake_profile?: string;
    cadence_picker?: boolean;
    single_display_offer?: boolean;
  };
}

export function ThankYouPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const sessionId = searchParams.get("session_id");
  const isRepFlow = Boolean(sessionId);

  const [countdown, setCountdown] = useState(10);
  const [selectionDone, setSelectionDone] = useState(false);
  const [selectedTier, setSelectedTier] = useState<PrequalResult | null>(null);
  const [termMonths, setTermMonths] = useState<number>(60);
  const [downPayment, setDownPayment] = useState(0);
  // Lever form state (time-in-business only; equipment lever removed)
  const [prior1099, setPrior1099] = useState(false);
  const [prior1099YearsInBusiness, setPrior1099YearsInBusiness] = useState<string>("");
  const [otherCompany, setOtherCompany] = useState(false);
  const [otherCompanyYearsInBusiness, setOtherCompanyYearsInBusiness] = useState<string>("");
  const [otherCompanyBusinessName, setOtherCompanyBusinessName] = useState<string>("");
  const [seasonalModalOpen, setSeasonalModalOpen] = useState(false);
  const [paymentCadence, setPaymentCadence] = useState<PaymentCadence>("monthly");

  const isInIframe = typeof window !== "undefined" && window.self !== window.top;

  const queryClient = useQueryClient();

  const { data: prequalData, isLoading: prequalLoading } = useQuery({
    queryKey: ["prequal-status", token, sessionId],
    queryFn: async (): Promise<PrequalData> => {
      if (!token) return {};
      if (isRepFlow && sessionId) {
        const { data } = await customInstance.get(`/api/intake/rep/${token}/prequal-status/`, {
          params: { session_id: sessionId },
        });
        return data;
      }
      const { data } = await customInstance.get(`/api/intake/invites/${token}/prequal-status/`);
      return {
        stage: data.stage,
        show_prequals_after_submit: data.show_prequals_after_submit,
        show_prequal_interest_and_apr: data.show_prequal_interest_and_apr,
        redirect_after_apply_url: data.redirect_after_apply_url ?? null,
        prequal_results: data.prequal_results,
        prequalified_at: data.prequalified_at,
        equipment_price: data.equipment_price,
        requested_amount: data.requested_amount,
        prequal_selection: data.prequal_selection,
        equipment_name: data.equipment_name,
        company_name: data.company_name,
        years_in_business: data.years_in_business ?? null,
        industry: data.industry,
        prequal_context: data.prequal_context,
        organization_logo_url: data.organization_logo_url,
        prequal_lever_answers: data.prequal_lever_answers,
        manual_review_flags: data.manual_review_flags ?? data.prequal_results?.manual_review_flags ?? [],
        credit_pull_status: data.credit_pull_status ?? null,
        credit_pull_error: data.credit_pull_error ?? null,
        upload_link_url: data.upload_link_url ?? null,
        manual_review_requested: data.manual_review_requested ?? false,
        submission_mode: data.submission_mode ?? "FULL_GUARANTOR",
        seasonal_payment_eligible: data.seasonal_payment_eligible ?? false,
        seasonal_payment_requests: data.seasonal_payment_requests ?? [],
        seasonal_payment_lender_ids: data.seasonal_payment_lender_ids ?? [],
        equipment_intake_pending: data.equipment_intake_pending ?? false,
        intake_phase: data.intake_phase,
        cadence_picker: data.cadence_picker ?? false,
        intake_program: data.intake_program ?? {},
      };
    },
    enabled: !!token && (isRepFlow ? !!sessionId : true),
    // Poll every 2 seconds while waiting for prequal results (credit pull is async)
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.submission_mode === "CORP_ONLY") {
        return false;
      }
      // Stop polling once we have prequal results or credit pull failed/completed
      if (data?.prequal_results || data?.credit_pull_status === "FAILED") {
        return false;
      }
      // Keep polling if credit pull is in progress or not started yet
      return 2000;
    },
  });

  const results = useMemo(() => prequalData?.prequal_results?.results ?? [], [prequalData?.prequal_results?.results]);
  const displayOffers = prequalData?.prequal_results?.display_offers;
  const matchedTiers = useMemo(
    () => (displayOffers?.length ? displayOffers : results.filter((r: PrequalResult) => r.matched)),
    [displayOffers, results]
  );
  const unmatchedTiers = useMemo(() => results.filter((r: PrequalResult) => !r.matched), [results]);
  // Show levers only when years in business is under 5 (customer can try to add prior 1099/entity to get more offers)
  const yearsInBusiness = prequalData?.years_in_business ?? null;
  const showLeverBlock = results.length > 0 && (yearsInBusiness == null || Number(yearsInBusiness) < 5);
  const showTibLever = useMemo(
    () =>
      showLeverBlock && (
        matchedTiers.length === 0 ||
        unmatchedTiers.some(
          (r) =>
            r.failure_code === "YEARS_IN_BUSINESS" ||
            (r.failure_reason ?? "").toLowerCase().includes("years_in_business")
        )
      ),
    [showLeverBlock, unmatchedTiers, matchedTiers.length]
  );

  const principal = useMemo(() => {
    const req = prequalData?.requested_amount ?? prequalData?.equipment_price ?? 0;
    return Number(req) || 0;
  }, [prequalData?.requested_amount, prequalData?.equipment_price]);

  // Down payment bounds: min from tier (pct or amount), max 50% of principal (unless tier requires more, then max >= min)
  const { minDownPayment, maxDownPayment } = useMemo(() => {
    let min = 0;
    if (selectedTier && principal > 0) {
      const fromPct = (selectedTier.min_down_payment_pct != null)
        ? principal * (Number(selectedTier.min_down_payment_pct) / 100)
        : 0;
      const fromAmt = (selectedTier.min_down_payment_amount != null)
        ? Number(selectedTier.min_down_payment_amount)
        : 0;
      min = Math.max(0, fromPct, fromAmt);
    }
    const fiftyPct = principal > 0 ? principal * 0.5 : 0;
    const max = principal > 0 ? Math.min(principal, Math.max(fiftyPct, min)) : 0;
    return { minDownPayment: min, maxDownPayment: Math.max(min, max) };
  }, [selectedTier, principal]);

  const clampedDownPayment = Math.max(minDownPayment, Math.min(maxDownPayment, downPayment));

  // Keep down payment within tier/50% bounds when tier or principal changes
  useEffect(() => {
    if (downPayment < minDownPayment || downPayment > maxDownPayment) {
      setDownPayment((prev) => Math.max(minDownPayment, Math.min(maxDownPayment, prev)));
    }
  }, [minDownPayment, maxDownPayment, downPayment]);

  const rateMid = selectedTier
    ? (selectedTier.rate_min + selectedTier.rate_max) / 2
    : 0;
  const financeAmount = financedPrincipalForPayment(principal, clampedDownPayment, selectedTier);
  const pointsPctForPmt = selectedTier?.total_points_used ?? 0;
  const principalForPayment = financeAmount * (1 + pointsPctForPmt / 100);
  const showCadencePicker = Boolean(prequalData?.cadence_picker);
  const isDemcoGrainCart = prequalData?.intake_program?.intake_profile === "demco_grain_cart";

  const lenderCadences = useMemo(
    () => enabledCadencesFromOffer(selectedTier),
    [selectedTier]
  );

  useEffect(() => {
    if (!selectedTier || !showCadencePicker) return;
    if (!lenderCadences.includes(paymentCadence)) {
      setPaymentCadence(lenderCadences[0] ?? "monthly");
    }
  }, [selectedTier, showCadencePicker, lenderCadences, paymentCadence]);

  const monthlyPaymentAmount = useMemo(
    () => (selectedTier && termMonths && financeAmount > 0 ? monthlyPayment(principalForPayment, rateMid, termMonths) : 0),
    [selectedTier, termMonths, financeAmount, rateMid, pointsPctForPmt]
  );

  const cadenceQuote = useMemo(() => {
    if (!showCadencePicker || !selectedTier || financeAmount <= 0 || termMonths <= 0) return null;
    const cfg = cadenceConfigFromOffer(selectedTier, paymentCadence);
    return computePaymentScheduleQuote({
      principalPrePoints: principal,
      downPayment: clampedDownPayment,
      annualRatePct: rateMid,
      termMonths,
      pointsPct: pointsPctForPmt,
      cadence: paymentCadence,
      advancePayment: Boolean(cfg.advance_payment),
      deferFirstMonths: cfg.defer_first_months ?? null,
    });
  }, [
    showCadencePicker,
    selectedTier,
    financeAmount,
    termMonths,
    principal,
    clampedDownPayment,
    rateMid,
    pointsPctForPmt,
    paymentCadence,
  ]);

  // Simple interest range: pre-points principal; payment = amortization of principal * (1 + points)
  const simpleInterestRange = useMemo(() => {
    if (!selectedTier || financeAmount <= 0 || termMonths <= 0) return { min: 0, max: 0 };
    const rmin = selectedTier.rate_min ?? 0;
    const rmax = selectedTier.rate_max ?? 0;
    const pointsPct = selectedTier.total_points_used ?? 0;
    const principalWithPoints = financeAmount * (1 + pointsPct / 100);
    const vals = [
      simpleInterestPct(financeAmount, monthlyPayment(principalWithPoints, rmin, termMonths), termMonths),
      simpleInterestPct(financeAmount, monthlyPayment(principalWithPoints, rmax, termMonths), termMonths),
    ].filter((x) => x >= 0 && isFinite(x));
    if (vals.length === 0) return { min: 0, max: 0 };
    return { min: Math.min(...vals), max: Math.max(...vals) };
  }, [selectedTier, financeAmount, termMonths]);

  // Final APR range (includes points): rate_min and rate_max with single points value
  const finalAprRange = useMemo(() => {
    if (!selectedTier || financeAmount <= 0 || termMonths <= 0) return { min: 0, max: 0 };
    const rmin = selectedTier.rate_min ?? 0;
    const rmax = selectedTier.rate_max ?? 0;
    const pointsPct = selectedTier.total_points_used ?? 0;
    const aprs = [
      finalAprIncludingPoints(financeAmount, rmin, termMonths, pointsPct),
      finalAprIncludingPoints(financeAmount, rmax, termMonths, pointsPct),
    ].filter((x) => x > 0 && isFinite(x));
    if (aprs.length === 0) return { min: rmin, max: rmax };
    return { min: Math.min(...aprs), max: Math.max(...aprs) };
  }, [selectedTier, financeAmount, termMonths]);

  const saveSelection = useMutation({
    mutationFn: async (payload: {
      lender_id: number;
      tier_id: number;
      term_months: number;
      down_payment_amount: number;
      monthly_payment: number;
      payment_cadence: PaymentCadence;
      payment_amount: number;
      total_payments?: number;
      due_at_signing?: number;
      first_payment_due_months?: number;
    }) => {
      if (isRepFlow && sessionId) {
        const { data } = await customInstance.post(`/api/intake/rep/${token}/prequal-selection/`, {
          ...payload,
          session_id: sessionId,
        });
        return data;
      }
      const { data } = await customInstance.post(`/api/intake/invites/${token}/prequal-selection/`, payload);
      return data;
    },
    onSuccess: () => {
      setSelectionDone(true);
      queryClient.invalidateQueries({ queryKey: ["prequal-status", token, sessionId] });
      toast.success("Selection saved.");
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err?.response?.data?.error ?? "Failed to save selection");
    },
  });

  const saveLeverAnswers = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isRepFlow && sessionId) {
        const { data } = await customInstance.post(`/api/intake/rep/${token}/prequal-lever-answers/`, {
          ...payload,
          session_id: sessionId,
        });
        return data;
      }
      const { data } = await customInstance.post(`/api/intake/invites/${token}/prequal-lever-answers/`, payload);
      return data;
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ["prequal-status", token, sessionId] });
      toast.success("Answers saved. Your offers have been updated.");
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err?.response?.data?.error ?? "Failed to save answers");
    },
  });

  const saveSeasonalPaymentRequest = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isRepFlow && sessionId) {
        const { data } = await customInstance.post(`/api/intake/rep/${token}/seasonal-payment-request/`, {
          ...payload,
          session_id: sessionId,
        });
        return data;
      }
      const { data } = await customInstance.post(`/api/intake/invites/${token}/seasonal-payment-request/`, payload);
      return data;
    },
    onSuccess: () => {
      setSeasonalModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["prequal-status", token, sessionId] });
      toast.success("Seasonal payment request saved. Our team will follow up with lenders.");
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err?.response?.data?.error ?? "Failed to save seasonal payment request");
    },
  });

  const requestManualReview = useMutation({
    mutationFn: async () => {
      if (isRepFlow && sessionId) {
        const { data } = await customInstance.post(`/api/intake/rep/${token}/request-manual-review/`, { session_id: sessionId });
        return data;
      }
      const { data } = await customInstance.post(`/api/intake/invites/${token}/request-manual-review/`);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["prequal-status", token, sessionId] });
      toast.success("Manual review requested. Our team will follow up.");
    },
    onError: (err: { response?: { data?: { error?: string } } }) => {
      toast.error(err?.response?.data?.error ?? "Failed to request manual review");
    },
  });

  const handleContinue = () => {
    if (!selectedTier) {
      toast.error("Please select an offer.");
      return;
    }
    const selectedPaymentAmount =
      showCadencePicker && cadenceQuote
        ? cadenceQuote.paymentAmount
        : monthlyPaymentAmount;
    saveSelection.mutate({
      lender_id: selectedTier.lender_id,
      tier_id: selectedTier.tier_id,
      term_months: termMonths,
      down_payment_amount: clampedDownPayment,
      // Keep monthly_payment for backward compatibility with existing consumers.
      monthly_payment: Math.round(selectedPaymentAmount * 100) / 100,
      payment_cadence: showCadencePicker ? paymentCadence : "monthly",
      payment_amount: Math.round(selectedPaymentAmount * 100) / 100,
      total_payments: cadenceQuote?.totalPayments,
      due_at_signing: cadenceQuote?.dueAtSigning,
      first_payment_due_months: cadenceQuote?.deferFirstMonths,
    });
  };

  const closeModal = () => {
    try {
      window.parent.postMessage({ type: "CLOSE_MODAL" }, "*");
    } catch (e) {
      console.error("Error closing modal:", e);
    }
  };

  useEffect(() => {
    if (isInIframe) {
      try {
        window.parent.postMessage({ type: "APPLICATION_SUBMITTED", message: "Application submitted successfully" }, "*");
        window.parent.postMessage({ type: "APPLICATION_SUCCESS", message: "Application submitted successfully" }, "*");
      } catch (e) {
        console.error("Error sending message to parent:", e);
      }
    }
  }, [isInIframe]);

  // Authorized but equipment not confirmed — resume Step 4 on apply
  useEffect(() => {
    if (!token || prequalLoading || !prequalData?.equipment_intake_pending) return;
    if (isRepFlow && sessionId) {
      navigate(`/apply/rep/${token}?session_id=${encodeURIComponent(sessionId)}`, { replace: true });
      return;
    }
    if (!isRepFlow) {
      navigate(`/apply/${token}`, { replace: true });
    }
  }, [token, sessionId, isRepFlow, prequalLoading, prequalData?.equipment_intake_pending, navigate]);

  // Redirect only when: not in iframe, org has prequal OFF, and org has a website URL set
  const shouldRedirect =
    !isInIframe &&
    prequalData?.show_prequals_after_submit === false &&
    (prequalData?.redirect_after_apply_url ?? "").trim() !== "";

  const redirectUrl = (prequalData?.redirect_after_apply_url ?? "").trim() || null;

  useEffect(() => {
    if (!shouldRedirect || !redirectUrl) return;
      const redirectTimer = setTimeout(() => {
      window.location.href = redirectUrl;
      }, 10000);
      const countdownInterval = setInterval(() => {
      setCountdown((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
      return () => {
        clearTimeout(redirectTimer);
        clearInterval(countdownInterval);
      };
  }, [shouldRedirect, redirectUrl]);

  useEffect(() => {
    if (!selectedTier) return;
    const terms = tierTermMonthsOrDefault(selectedTier.term_months);
    setTermMonths((prev) => (terms.includes(prev) ? prev : Math.max(...terms)));
  }, [selectedTier]);

  useEffect(() => {
    if (matchedTiers.length === 1 && !selectedTier) {
      const tier = matchedTiers[0];
      setSelectedTier(tier);
      setPaymentCadence(preferredCadenceFromOffer(tier, isDemcoGrainCart));
    }
  }, [matchedTiers, selectedTier, isDemcoGrainCart]);

  const manualReviewRequested = prequalData?.manual_review_requested ?? false;

  const showPrequalPicker =
    token &&
    prequalData?.show_prequals_after_submit &&
    matchedTiers.length > 0 &&
    !prequalData?.prequal_selection &&
    !selectionDone &&
    !manualReviewRequested;

  const showNoOffersWithLever =
    token &&
    prequalData?.show_prequals_after_submit &&
    prequalData?.stage === "RECEIVED" &&
    prequalData?.prequal_results?.results &&
    results.length > 0 &&
    matchedTiers.length === 0 &&
    !manualReviewRequested;

  const alreadyAnsweredLever = Boolean(prequalData?.prequal_lever_answers?.submitted_at);
  const manualReviewFlags = prequalData?.manual_review_flags ?? [];
  const isCorpOnlySubmission =
    prequalData?.submission_mode === "CORP_ONLY" || manualReviewFlags.includes("CORP_ONLY");
  const showManualReviewBanner = manualReviewFlags.includes("EQUIPMENT_AGE_PRE_2005");
  const creditPullFailed = prequalData?.credit_pull_status === "FAILED";
  const uploadLinkUrl = prequalData?.upload_link_url ?? null;

  const hasSelectedOffer =
    selectionDone || Boolean(prequalData?.prequal_selection) || manualReviewRequested;

  // Show "Preparing your prequalified offers" when: initial load, or waiting for async credit pull + prequal
  const waitingForOffers =
    token &&
    prequalData?.show_prequals_after_submit === true &&
    !prequalData?.prequal_results &&
    prequalData?.credit_pull_status !== "FAILED" &&
    !hasSelectedOffer &&
    !isCorpOnlySubmission;

  if (token && isCorpOnlySubmission && !prequalLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-amber-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="flex justify-center">
            <div className="bg-green-100 rounded-full p-4">
              <CheckCircle2 className="w-16 h-16 text-green-600" />
            </div>
          </div>
          {prequalData?.organization_logo_url && (
            <div className="flex justify-center">
              <img
                src={prequalData.organization_logo_url}
                alt="Organization logo"
                className="max-h-48 max-w-lg w-auto object-contain"
              />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900 text-center">Application received</h1>
          <Card className="border-primary-200 bg-primary-50/50">
            <CardHeader>
              <CardTitle className="text-lg text-gray-900">Corporate-only submission</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-gray-800">
              <p>
                Thank you — we&apos;ve received your corporate-only application. Our team will review it manually
                and follow up with next steps.
              </p>
              <p className="mt-2">
                You do not need to take any action. A finance professional will contact you shortly.
              </p>
            </CardContent>
          </Card>
          {uploadLinkUrl && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">You can upload documents now:</p>
              <Button asChild className="bg-primary hover:bg-primary-600 text-white">
                <a href={uploadLinkUrl} target="_blank" rel="noopener noreferrer">
                  Upload documents
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (token && (prequalLoading || waitingForOffers)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-amber-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 flex flex-col items-center justify-center gap-4 text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary rounded-full animate-spin" aria-hidden />
          <h2 className="text-lg font-semibold text-gray-900">Thank you — we've received your application</h2>
          <p className="text-sm text-gray-600">Preparing your prequalified offers. This may take a moment…</p>
        </div>
      </div>
    );
  }

  // Credit pull failed — show dedicated message and next steps (not for corp-only)
  if (token && prequalData?.stage === "RECEIVED" && creditPullFailed && !isCorpOnlySubmission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-amber-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 space-y-6">
          <div className="flex justify-center">
            <div className="bg-amber-100 rounded-full p-4">
              <CheckCircle2 className="w-16 h-16 text-amber-600" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 text-center">Application received</h1>
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader>
              <CardTitle className="text-lg text-amber-900">Credit pull could not be completed</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-amber-900">
              <p>We were unable to pull credit automatically. Our team will follow up with you to complete the process.</p>
              {prequalData?.credit_pull_error && (
                <p className="text-amber-800 font-medium mt-2">Details: {String(prequalData.credit_pull_error)}</p>
              )}
              <p className="mt-4">You do not need to take any action. A finance professional will contact you shortly.</p>
            </CardContent>
          </Card>
          {uploadLinkUrl && (
            <div className="text-center">
              <p className="text-sm text-gray-600 mb-2">You can upload documents now:</p>
              <Button asChild className="bg-primary hover:bg-primary-600 text-white">
                <a href={uploadLinkUrl} target="_blank" rel="noopener noreferrer">Upload documents</a>
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const handleSubmitLever = () => {
    const payload: Record<string, unknown> = {};
    if (showTibLever) {
      payload.prior_1099_or_sole_prop = prior1099;
      if (prior1099 && prior1099YearsInBusiness.trim()) {
        const y = parseInt(prior1099YearsInBusiness.trim(), 10);
        if (!Number.isNaN(y)) payload.updated_years_in_business = y;
      }
      payload.other_company_similar_industry = otherCompany;
      if (otherCompany) {
        if (otherCompanyYearsInBusiness.trim()) {
          const y = parseInt(otherCompanyYearsInBusiness.trim(), 10);
          if (!Number.isNaN(y)) payload.other_company_years_in_business = y;
        }
        if (otherCompanyBusinessName.trim()) payload.new_business_name = otherCompanyBusinessName.trim();
      }
    }
    saveLeverAnswers.mutate(payload);
  };

  function renderLeverBlock() {
    if (!showLeverBlock) return null;
    const hasMatches = matchedTiers.length > 0;
    return (
      <Card className="border-primary-200 bg-white/90">
        <CardHeader>
          <CardTitle className="text-lg">
            {hasMatches
              ? "If you have documentation showing a longer time in business — or an affiliated company with an established track record in the same industry — you may qualify for a better offer."
              : "If you have documentation showing a longer time in business — or an affiliated company with an established track record in the same industry — you may qualify for a better offer."}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {alreadyAnsweredLever ? (
            <p className="text-sm text-gray-600 font-medium">Already answered. Your offers above have been updated based on your answers.</p>
          ) : (
            <>
              {showTibLever && (
                <div className="space-y-3">
                  <p className="text-sm font-semibold text-gray-900">Time in business</p>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={prior1099}
                      onChange={(e) => setPrior1099(e.target.checked)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    I have a 1099/Schedule C/Schedule F from more than 2 years ago and I can prove it:
                  </label>
                  {prior1099 && (
                    <div>
                      <Label className="text-sm">Updated years in business</Label>
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="e.g. 5"
                        value={prior1099YearsInBusiness}
                        onChange={(e) => setPrior1099YearsInBusiness(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={otherCompany}
                      onChange={(e) => setOtherCompany(e.target.checked)}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                    Related business entity operating in a similar or adjacent industry with a longer time in business
                  </label>
                  {otherCompany && (
                    <div className="space-y-2">
                      <div>
                        <Label className="text-sm">Updated years in business</Label>
                        <Input
                          type="text"
                          inputMode="numeric"
                          placeholder="e.g. 10"
                          value={otherCompanyYearsInBusiness}
                          onChange={(e) => setOtherCompanyYearsInBusiness(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">New Business Name</Label>
                        <Input
                          type="text"
                          placeholder="Company name"
                          value={otherCompanyBusinessName}
                          onChange={(e) => setOtherCompanyBusinessName(e.target.value)}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
              <Button
                onClick={handleSubmitLever}
                disabled={saveLeverAnswers.isPending || !showTibLever}
                className="bg-primary hover:bg-primary-600 text-white"
              >
                {saveLeverAnswers.isPending ? "Saving…" : "Submit answers & refresh offers"}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  function renderCreditSummary(snippet: CreditSnippet, title?: string) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200">
        <p className="text-base font-bold text-gray-900 uppercase tracking-wide mb-2">{title ?? "Credit summary"}</p>
        <ul className="text-sm text-gray-700 space-y-1">
          {snippet.fico_score != null && (
            <li><span className="font-semibold text-gray-900">FICO Score:</span> {snippet.fico_score}</li>
          )}
          {snippet.revolving_usage_pct != null && (
            <li><span className="font-semibold text-gray-900">Current revolving usage:</span> {Number(snippet.revolving_usage_pct).toFixed(1)}%</li>
          )}
          {snippet.comparable_installment_debt_24m != null && snippet.comparable_installment_debt_24m > 0 && (
            <li><span className="font-semibold text-gray-900">Comparable installment debt (24 mo):</span> ${Number(snippet.comparable_installment_debt_24m).toLocaleString("en-US", { minimumFractionDigits: 2 })}</li>
          )}
          <li>
            <span className="font-semibold text-gray-900">Current mortgage balance:</span>{" "}
            {snippet.mortgage_balance != null && snippet.mortgage_balance > 0
              ? `$${Number(snippet.mortgage_balance).toLocaleString("en-US", { minimumFractionDigits: 2 })}`
              : "None"}
          </li>
          {snippet.chargeoffs_display && (
            <li><span className="font-semibold text-gray-900">Chargeoffs:</span> {snippet.chargeoffs_display}</li>
          )}
          {snippet.collections_display && (
            <li><span className="font-semibold text-gray-900">Collections:</span> {snippet.collections_display}</li>
          )}
          {snippet.repos_foreclosures_display && (
            <li><span className="font-semibold text-gray-900">Repossessions &amp; foreclosures:</span> {snippet.repos_foreclosures_display}</li>
          )}
        </ul>
      </div>
    );
  }

  if (showNoOffersWithLever) {
    const ctx = prequalData?.prequal_context;
    const creditSnippets = ctx?.credit_snippets;
    const singleSnippet = ctx?.credit_snippet;
    const companyName = prequalData?.company_name ?? ctx?.company_name ?? "";
    const industry = prequalData?.industry ?? ctx?.industry ?? "";
    const equipmentName = prequalData?.equipment_name ?? ctx?.equipment_name ?? "";
    const tibDisplay = ctx?.time_in_bureau_display ?? singleSnippet?.time_in_bureau ?? "N/A";
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-amber-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full space-y-6">
          {prequalData?.organization_logo_url && (
            <div className="flex justify-center">
              <img
                src={prequalData.organization_logo_url}
                alt="Organization logo"
                className="max-h-48 max-w-lg w-auto object-contain"
              />
            </div>
          )}
          <Card className="border-primary-200 bg-white/90">
            <CardContent className="pt-4 pb-4 space-y-3">
              {companyName && <p className="text-center font-semibold text-gray-900">{companyName}</p>}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-gray-700">
                {tibDisplay && <span><span className="font-semibold text-gray-900">Time in bureau:</span> {tibDisplay}</span>}
                {industry && <span><span className="font-semibold text-gray-900">Industry:</span> {industry}</span>}
                {equipmentName && <span><span className="font-semibold text-gray-900">Equipment:</span> {equipmentName}</span>}
              </div>
              {creditSnippets && creditSnippets.length > 0 ? (
                creditSnippets.map((item, idx) => (
                  <div key={idx}>
                    {renderCreditSummary(item.credit_snippet, item.display_name ? `Credit summary — ${item.display_name}` : "Credit summary")}
                  </div>
                ))
              ) : singleSnippet ? (
                renderCreditSummary(singleSnippet)
              ) : null}
            </CardContent>
          </Card>
          <p className="text-center text-gray-600">
            {showLeverBlock
              ? "We could not match any offers yet. Answer the questions below to see if we can find better options."
              : "We were not able to find an automatic match for this application. Our team can review it manually and find the right program."}
          </p>
          {renderLeverBlock()}
          {!manualReviewRequested && (
            <Card className="border-gray-200 mt-4">
              <CardContent className="pt-4 pb-4">
                <p className="text-sm text-gray-600 mb-2">Prefer to have a person review this?</p>
                <Button
                  variant="outline"
                  onClick={() => requestManualReview.mutate()}
                  disabled={requestManualReview.isPending}
                  className="w-full"
                >
                  {requestManualReview.isPending ? "Requesting…" : "Request manual review"}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  if (showPrequalPicker) {
    const ctx = prequalData?.prequal_context;
    const creditSnippets = ctx?.credit_snippets;
    const singleSnippet = ctx?.credit_snippet;
    const companyName = prequalData?.company_name ?? ctx?.company_name ?? "";
    const industry = prequalData?.industry ?? ctx?.industry ?? "";
    const equipmentName = prequalData?.equipment_name ?? ctx?.equipment_name ?? "";
    const tibDisplay = ctx?.time_in_bureau_display ?? singleSnippet?.time_in_bureau ?? "N/A";

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-amber-50 flex items-center justify-center p-4">
        <div className="max-w-2xl w-full space-y-6">
          {/* Org logo above the summary box */}
          {prequalData?.organization_logo_url && (
            <div className="flex justify-center">
              <img
                src={prequalData.organization_logo_url}
                alt="Organization logo"
                className="max-h-48 max-w-lg w-auto object-contain"
              />
            </div>
          )}
          {/* Top section: Biz name, Time in bureau, Industry, Equipment, credit snippet */}
          <Card className="border-primary-200 bg-white/90">
            <CardContent className="pt-4 pb-4 space-y-3">
              {companyName && (
                <p className="text-center font-semibold text-gray-900">{companyName}</p>
              )}
              <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 text-sm text-gray-700">
                {tibDisplay && (
                  <span><span className="font-semibold text-gray-900">Time in bureau:</span> {tibDisplay}</span>
                )}
                {industry && (
                  <span><span className="font-semibold text-gray-900">Industry:</span> {industry}</span>
                )}
                {equipmentName && (
                  <span><span className="font-semibold text-gray-900">Equipment:</span> {equipmentName}</span>
                )}
              </div>
              {creditSnippets && creditSnippets.length > 0 ? (
                creditSnippets.map((item, idx) => (
                  <div key={idx}>
                    {renderCreditSummary(item.credit_snippet, item.display_name ? `Credit summary — ${item.display_name}` : "Credit summary")}
                  </div>
                ))
              ) : singleSnippet ? (
                renderCreditSummary(singleSnippet)
              ) : null}
            </CardContent>
          </Card>

          {showManualReviewBanner && (
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="pt-4 pb-4">
                <p className="text-sm text-amber-900">
                  <strong>Manual review may apply.</strong> Your equipment year is before 2005. Our team may need to review your application manually. You can request a manual review below if you don't see an offer.
                </p>
              </CardContent>
            </Card>
          )}

          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 text-center">
            {matchedTiers.length === 1 ? "Your prequalified offer" : "Your prequalified offers"}
          </h1>
          <p className="text-gray-600 text-center text-sm">
            {matchedTiers.length === 1
              ? "Review your offer and adjust term and down payment to see your estimated payment."
              : "Choose an offer, then adjust term and down payment to see your estimated monthly payment."}
          </p>

          <div className="grid gap-3">
            {matchedTiers.length > 1 && <Label className="text-sm font-medium">Select an offer</Label>}
            <div className={`grid gap-2 ${matchedTiers.length > 1 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1"}`}>
              {matchedTiers.map((tier, idx) => (
                <Fragment key={`${tier.lender_id}-${tier.tier_id}`}>
                  <Card
                    className={`cursor-pointer transition-all ${
                      selectedTier?.tier_id === tier.tier_id && selectedTier?.lender_id === tier.lender_id
                        ? "ring-2 ring-primary bg-primary-50/50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => {
                      if (selectedTier?.tier_id === tier.tier_id && selectedTier?.lender_id === tier.lender_id) {
                        setSelectedTier(null);
                      } else {
                        setSelectedTier(tier);
                        const terms = tierTermMonthsOrDefault(tier.term_months);
                        setTermMonths(Math.max(...terms));
                        setPaymentCadence(preferredCadenceFromOffer(tier, isDemcoGrainCart));
                      }
                    }}
                  >
                    <CardHeader className="p-3 pb-2">
                      <CardTitle className="text-sm font-medium">
                        Offer {idx + 1}
                      </CardTitle>
                      {(tier.display_bucket || tier.display_rank_in_bucket) && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {tier.display_bucket === "no_down_payment" ? "No down payment" : tier.display_bucket === "down_payment_required" ? "Down payment required" : ""}
                          {tier.display_bucket && tier.display_rank_in_bucket ? " · " : ""}
                          {tier.display_rank_in_bucket === 1 ? "Best match" : tier.display_rank_in_bucket === 2 ? "Also recommended" : ""}
                        </p>
                      )}
                      {tier.equipment_requirements && (
                        <p className="text-xs text-gray-600 border-t pt-2 mt-2">
                          {tier.equipment_requirements}
                        </p>
                      )}
                      {tier.required_stipulation_templates && tier.required_stipulation_templates.length > 0 && (
                        <p className="text-xs text-amber-700 border-t pt-2 mt-2">
                          <span className="font-medium">Required: </span>
                          {tier.required_stipulation_templates.map((s: { id: number; name: string }) => s.name).join(", ")}
                        </p>
                      )}
                    </CardHeader>
                  </Card>
                  {selectedTier?.tier_id === tier.tier_id && selectedTier?.lender_id === tier.lender_id && (
                    <div className="sm:col-span-2 space-y-4 pt-2">
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Term length</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          <div className="flex flex-wrap gap-2">
                            {tierTermMonthsOrDefault(selectedTier.term_months).map((term) => (
                              <button
                                key={term}
                                type="button"
                                onClick={() => setTermMonths(term)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                  termMonths === term
                                    ? "bg-primary text-white"
                                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                              >
                                {term} months
                              </button>
                            ))}
                          </div>
                        </CardContent>
                      </Card>

                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-base">Down payment</CardTitle>
                          <p className="text-sm font-normal text-gray-600">
                            ${Math.round(clampedDownPayment).toLocaleString()} of ${principal.toLocaleString()} (financing ${financeAmount.toLocaleString()})
                          </p>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <input
                            type="range"
                            min={minDownPayment}
                            max={maxDownPayment}
                            step={100}
                            value={clampedDownPayment}
                            onChange={(e) => setDownPayment(Number(e.target.value))}
                            className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-gray-200 accent-primary"
                          />
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>${Math.round(minDownPayment).toLocaleString()}</span>
                            <span>${Math.round(maxDownPayment).toLocaleString()}</span>
                          </div>
                        </CardContent>
                      </Card>

                      {showCadencePicker && (
                        <Card>
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base">Payment cadence</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <select
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                              value={paymentCadence}
                              onChange={(e) => setPaymentCadence(e.target.value as PaymentCadence)}
                            >
                              {lenderCadences.map((c) => (
                                <option key={c} value={c}>
                                  {CADENCE_LABELS[c]}
                                </option>
                              ))}
                            </select>
                          </CardContent>
                        </Card>
                      )}

                      <div className="bg-white rounded-xl border-2 border-primary-200 p-6 text-center space-y-4">
                        <div>
                          <p className="text-sm text-gray-600 mb-1">
                            {showCadencePicker && paymentCadence !== "monthly"
                              ? `Est. ${CADENCE_LABELS[paymentCadence].toLowerCase()} pmt`
                              : "Est. monthly pmt"}
                          </p>
                          <p className="text-3xl md:text-4xl font-bold text-gray-900">
                            $
                            {(showCadencePicker && cadenceQuote
                              ? cadenceQuote.paymentAmount
                              : monthlyPaymentAmount
                            ).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {showCadencePicker && cadenceQuote && paymentCadence !== "monthly"
                              ? cadenceQuote.advancePayment
                                ? `${cadenceQuote.totalPayments} payments · ${cadenceQuote.dueAtSigning > 0 ? `$${cadenceQuote.dueAtSigning.toLocaleString()} due at signing` : ""}`
                                : `${cadenceQuote.totalPayments} payments · first due in ${cadenceQuote.deferFirstMonths} mo`
                              : `${termMonths} months`}
                          </p>
                        </div>
                        {prequalData?.show_prequal_interest_and_apr !== false && (
                          <>
                            <div className="pt-3 border-t border-primary-100">
                              <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Interest rate</p>
                              <p className="text-lg font-bold text-primary-700">
                                {simpleInterestRange.min.toFixed(2)}–{simpleInterestRange.max.toFixed(2)}%
                              </p>
                            </div>
                            <div className="pt-2">
                              <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">APR</p>
                              <p className="text-sm font-medium text-gray-800">
                                {finalAprRange.min.toFixed(1)}–{finalAprRange.max.toFixed(1)}%
                              </p>
                            </div>
                          </>
                        )}
                        <p className="text-[11px] text-gray-500 leading-snug pt-2 border-t border-primary-100">
                          {PAYMENT_ESTIMATE_DISCLAIMER}
                        </p>
                        {selectedTier.approval_card_note && (
                          <p className="text-sm text-gray-600 italic pt-3 border-t border-primary-100">
                            {selectedTier.approval_card_note}
                          </p>
                        )}
                        {selectedTier.ownership_100_pct_note && (
                          <p className="text-sm text-amber-800 italic pt-2">
                            {selectedTier.ownership_100_pct_note}
                          </p>
                        )}
                      </div>

                      {prequalData?.seasonal_payment_eligible && selectedTier && !isDemcoGrainCart && (
                        <>
                          <SeasonalPaymentRequestButton
                            className="w-full"
                            existing={(prequalData.seasonal_payment_requests ?? []).find(
                              (r) => r.lender_id === selectedTier.lender_id
                            )}
                            onClick={() => setSeasonalModalOpen(true)}
                          />
                          {(prequalData.seasonal_payment_requests ?? []).some(
                            (r) => r.lender_id === selectedTier.lender_id
                          ) && (
                            <p className="text-xs text-green-700 text-center">
                              Seasonal payment request on file for this offer.
                            </p>
                          )}
                        </>
                      )}

                      <Button
                        onClick={handleContinue}
                        disabled={saveSelection.isPending}
                        className="w-full bg-primary hover:bg-primary-600 text-white"
                      >
                        {saveSelection.isPending ? "Saving…" : "Continue"}
                      </Button>

                      <p className="text-gray-400 text-[11px] leading-snug text-center">
                        Offers are subject to final underwriting. Terms may change based on new information or if required documentation cannot be provided or confirmed.
                      </p>

                      {!isDemcoGrainCart && (
                        <Button
                          variant="ghost"
                          className="w-full text-gray-600"
                          onClick={() => setSelectedTier(null)}
                        >
                          Choose a different offer
                        </Button>
                      )}
                    </div>
                  )}
                </Fragment>
              ))}
            </div>
          </div>

          {showLeverBlock && renderLeverBlock()}

          {prequalData?.seasonal_payment_eligible && selectedTier && !isDemcoGrainCart && (
            <SeasonalPaymentRequestModal
              open={seasonalModalOpen}
              onOpenChange={setSeasonalModalOpen}
              lenderId={selectedTier.lender_id}
              tierId={selectedTier.tier_id}
              termMonths={termMonths}
              downPaymentAmount={clampedDownPayment}
              monthlyPayment={Math.round(monthlyPaymentAmount * 100) / 100}
              existing={(prequalData.seasonal_payment_requests ?? []).find(
                (r) => r.lender_id === selectedTier.lender_id
              )}
              isPending={saveSeasonalPaymentRequest.isPending}
              onSubmit={(payload) => saveSeasonalPaymentRequest.mutate(payload)}
            />
          )}

          {!manualReviewRequested && (
            <Card className="border-gray-200 mt-6">
              <CardContent className="pt-4 pb-4">
                <p className="text-sm text-gray-600 mb-2">Don't see an offer that works for you?</p>
                <Button
                  variant="outline"
                  onClick={() => requestManualReview.mutate()}
                  disabled={requestManualReview.isPending}
                  className="w-full"
                >
                  {requestManualReview.isPending ? "Requesting…" : "Request manual review"}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    );
  }

  const showPostSelectionActions =
    token &&
    (prequalData?.stage === "RECEIVED" || prequalData?.stage === "IN_REVIEW") &&
    hasSelectedOffer;

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-amber-50 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center">
        <div className="flex justify-center mb-6">
          <div className="bg-green-100 rounded-full p-4">
            <CheckCircle2 className="w-16 h-16 text-green-600" />
          </div>
        </div>
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Thank You!</h1>
        <p className="text-xl md:text-2xl text-gray-700 mb-6">Your application has been received</p>
        <div className="bg-primary-50 border-l-4 border-primary p-6 rounded-r-lg mb-8">
          <p className="text-lg text-gray-800 mb-2 font-medium">What happens next?</p>
          <p className="text-gray-600">
            A finance professional from our team will review your application and reach out to you shortly to discuss your financing options.
          </p>
        </div>
        {showPostSelectionActions && (
          <div className="mb-8 space-y-4 text-left">
            {uploadLinkUrl && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Upload documents</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-3">
                    Please upload your driver's license, voided check, and any other requested documents so we can process your application quickly.
                  </p>
                  <Button asChild className="bg-primary hover:bg-primary-600 text-white">
                    <a href={uploadLinkUrl} target="_blank" rel="noopener noreferrer">Go to upload page</a>
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        {shouldRedirect && redirectUrl && (
          <div className="text-sm text-gray-500">
            <p>
              Redirecting to{" "}
              <a href={redirectUrl} className="text-primary-600 hover:text-primary-800 underline" target="_blank" rel="noopener noreferrer">
                our website
              </a>{" "}
              in {countdown} second{countdown !== 1 ? "s" : ""}…
            </p>
            <p className="mt-2">
              <a href={redirectUrl} className="text-primary-600 hover:text-primary-800 underline font-medium" target="_blank" rel="noopener noreferrer">
                Click here if you are not redirected automatically
              </a>
            </p>
          </div>
        )}
        {isInIframe && (
          <div className="mt-6">
            <Button onClick={closeModal} className="bg-primary hover:bg-primary-600 text-white">
              Close
            </Button>
            <p className="text-sm text-gray-500 mt-3">You can close this window when you&apos;re done.</p>
          </div>
        )}
      </div>
    </div>
  );
}
